/**
 * DataLayer Service
 * Core service for pushing events to the dataLayer.
 * Framework-agnostic implementation that works with Angular, AngularJS, and vanilla JS/TS.
 */

import {BaseEvent, FormInteractionType} from './types/common';
import {
    FormStart,
    FormInteraction,
    FormSuccess,
} from './types/form-events';
import { PageView } from './types/page-events';
import {
    buildFormStartEvent,
    buildFormInteractionEvent,
    buildFormSuccessEvent,
} from './events/form-event-builder';
import { buildPageViewEvent } from './events/page-event-builder';

// Extend Window interface for dataLayer
declare global {
    interface Window {
        dataLayer: unknown[];
        DataLayerService: typeof DataLayerService;
    }
}

/**
 * DataLayerService
 *
 * A static service class that provides methods to push tracking events
 * to the Google Tag Manager dataLayer.
 *
 * @example
 * // Basic usage
 * DataLayerService.sendFormStartEvent({
 *     formName: FormName.ConsumerQuoteRequest,
 *     formId: 'abc123'
 * });
 *
 * @example
 * // Enable debugging
 * // Visit page with #enable-ga-debug hash, or run:
 * // localStorage.setItem('ga-debug', '1');
 * // AND ensure data-ga-debug is on the body tag
 *
 * @example
 * // Page view with auto-detected AB test variant
 * DataLayerService.sendPageViewEvent({
 *     audienceType: 'consumer',
 *     pageType: 'form',
 *     pageTemplate: 'quote_request',
 *     locale: 'en_GB',
 *     businessUnit: 'MOVE',
 *     brandId: 1,
 *     brandName: 'SkyDreams'
 * });
 */
export class DataLayerService {
    /**
     * Enable or disable debug logging of events to the console.
     * When true and localStorage['ga-debug'] is set, events will be logged.
     */
    private static allowDebugging = false;

    /**
     * Get or initialize the dataLayer array
     */
    private static getDataLayer(): unknown[] {
        if (typeof window === 'undefined') {
            console.warn('DataLayerService: window is not defined');
            return [];
        }

        window.dataLayer = window.dataLayer || [];
        return window.dataLayer;
    }

    /**
     * Push any event to the dataLayer
     */
    public static push(event: unknown): void {
        const dataLayer = this.getDataLayer();
        dataLayer.push(event);
        this.debugEvent(event);
    }

    /**
     * Push a typed event to the dataLayer
     */
    public static pushEvent<T extends BaseEvent>(event: T): void {
        this.push(event);
    }

    /**
     * Send a form_start event
     *
     * @example
     * DataLayerService.sendFormStartEvent({
     *     formName: FormName.ConsumerQuoteRequest,
     *     formId: document.querySelector('[data-authcode]')?.getAttribute('data-authcode')
     * });
     */
    public static sendFormStartEvent<T extends FormStart>(options: T): void {
        const event = buildFormStartEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a form_start event with Promise-based callback
     * Useful when you need to wait for GTM to process the event before proceeding
     *
     * @param options Form start options
     * @param timeoutMs Timeout in milliseconds (default: 500ms)
     * @returns Promise that resolves when GTM fires callback or times out
     */
    public static sendFormStartEventAsync<T extends FormStart>(
        options: T,
        timeoutMs: number = 500
    ): Promise<void> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                console.warn('DataLayerService: GTM callback did not fire in time');
                resolve();
            }, timeoutMs);

            const event = buildFormStartEvent(options);
            const eventWithCallback = {
                ...event,
                eventCallback: () => {
                    clearTimeout(timeout);
                    resolve();
                },
            };

            this.push(eventWithCallback);
        });
    }

    /**
     * Send a form_interaction event
     *
     * @example
     * DataLayerService.sendFormInteractionEvent({
     *     formName: FormName.ConsumerQuoteRequest,
     *     formInteraction: 'view',
     *     formStepName: 'address_details',
     *     formStep: 1,
     *     formTotalSteps: 3,
     *     formPhaseName: 'address',
     *     formPhase: 1,
     *     formTotalPhases: 3
     * });
     */
    public static sendFormInteractionEvent<T extends FormInteraction>(options: T): void {
        // Validate form_interaction type
        if (!Object.values(FormInteractionType).includes(options.formInteraction as FormInteractionType)) {
            console.warn(`DataLayerService: Invalid form interaction type '${options.formInteraction}'. Allowed types are: ${Object.values(FormInteractionType).join(', ')}`);
            return;
        }

        const event = buildFormInteractionEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a form_interaction event with error data
     * @deprecated Use sendFormInteractionEvent with formInteraction: FormInteractionType.ERROR and errorData property
     */
    public static sendFormErrorEvent<T extends FormInteraction>(options: T): void {
        this.sendFormInteractionEvent(options);
    }

    /**
     * Send a form_success event
     *
     * @example
     * DataLayerService.sendFormSuccessEvent({
     *     formName: FormName.ConsumerQuoteRequest,
     *     formId: 'abc123',
     *     formVersionId: 'v1',
     *     emailHash: 'sha256hash...',
     *     businessUnit: 'MOVE',
     *     company: {
     *         companyId: 123,
     *         companyName: 'Test Company',
     *         businessType: 'moving',
     *         contactEmail: 'test@example.com',
     *         contactTelephone: '+3112345678',
     *         productGroup: 'moving'
     *     },
     *     companyAddress: {
     *         countryIso: 'NL'
     *     },
     *     leadDetails: {
     *         targetingArea: {
     *             type: TargetingAreaType.Radius,
     *             unitSystem: 'metric',
     *             radiusValue: 50
     *         }
     *     }
     * });
     */
    public static sendFormSuccessEvent<T extends FormSuccess>(options: T): void {
        const event = buildFormSuccessEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a page view event
     * If ab_test_variant is not provided, attempts to read from body[data-ab]
     *
     * @example
     * DataLayerService.sendPageViewEvent({
     *     audienceType: 'consumer',
     *     pageType: 'category',
     *     pageTemplate: 'moving',
     *     locale: 'en_GB',
     *     businessUnit: 'MOVE',
     *     brandId: 1,
     *     brandName: 'SkyDreams',
     *     categoryId: 5,
     *     // categoryName is not in PageViewOptions, check types if needed
     * });
     */
    public static sendPageViewEvent<T extends PageView>(options: T): void {
        const event = buildPageViewEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a custom event with arbitrary data
     * Use this for events that don't fit the standard patterns
     */
    public static sendCustomEvent(eventName: string, data: Record<string, unknown> = {}): void {
        this.push({
            event: eventName,
            ...data,
        });
    }

    /**
     * Log event to the console for debugging purposes.
     * Only logs when allowDebugging are true and localStorage['ga-debug'] is set.
     */
    private static debugEvent(event: unknown): void {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            if (!this.allowDebugging || !window.localStorage.getItem('ga-debug')) {
                return;
            }
        } catch {
            return;
        }

        // eslint-disable-next-line no-console
        console.info('GA event pushed:', JSON.stringify(event, null, 2));
    }

    /**
     * Initialize debug mode from the URL hash.
     * Call this on page load to enable debugging when #enable-ga-debug is present.
     */
    public static initDebugMode(): void {
        if (typeof window === 'undefined') {
            return;
        }

        const checkBody = () => {
            if (document.body && document.body.hasAttribute('data-ga-debug')) {
                this.allowDebugging = true;
            }
        };

        if (document.body) {
            checkBody();
        } else {
            window.addEventListener('DOMContentLoaded', checkBody);
        }

        try {
            if (window.location.hash === '#enable-ga-debug') {
                window.localStorage.setItem('ga-debug', '1');
                // eslint-disable-next-line no-console
                console.info('GA debug enabled');
            }
        } catch {
            // Storage blocked in this context (e.g. Safari with cookies disabled, private mode)
        }
    }
}

// Expose on window for non-module contexts (AngularJS, legacy scripts)
if (typeof window !== 'undefined') {
    // todo: migrate this to skyComponents namespace
    window.DataLayerService = DataLayerService;

    // Auto-initialize debug mode from URL hash
    DataLayerService.initDebugMode();
}

